import {
    h, RefCallback, Component, createRef, ComponentChildren
} from "preact";

import { formatTextArea } from "../../../../apps/legacy/richText";

import CSSProperties from "../../../../foundation/model/CSSProperties";
import classname from "../../../../foundation/utils/classname";
import { stopPropagation } from "../../../../foundation/utils/legacy";
import { highlightInput } from "../../../../foundation/utils/platform";

import SectionElevator, { SectionElevatorProps } from "../../../SectionElevator";
import IconArrowDown from "../../Icons/IconArrowDown";
import IconDragSmall from "../../Icons/IconDragSmall";
import RichInput, { RichInputBlurData } from "../../Input/RichInput";
import { InputVariant } from "../../Input";
import OnlyIconButton, { OnlyIconButtonVariant } from "../../OnlyIconButton";

import styles from "./SectionHeader.module.css";

interface Props {
    class?: string;
    style?: CSSProperties;
    id: string;
    name: string;
    description?: string;
    descriptionContent?: ComponentChildren;
    summaryContent?: ComponentChildren;
    info?: ComponentChildren | null;
    infoText?: string;
    emptyText?: string;
    filterValue?: string;
    depth?: number;
    maximumDepthLimit?: number;
    // Actions
    elevatorProps?: SectionElevatorProps;
    optionsMenuButton?: ComponentChildren;
    additionalActionButtons?: ComponentChildren;
    additionalInfo?: ComponentChildren;
    // Flags
    isToggleDisabled?: boolean;
    actionsDisabled?: boolean;
    readOnly?: boolean;
    collapsed?: boolean;
    hasDragHover?: boolean;
    showDescription?: boolean;
    expandOnHeaderClick?: boolean;
    // Callbacks
    onNameBlur?: (data: RichInputBlurData<string>) => void;
    onDescriptionBlur?: (data: RichInputBlurData<string>) => void;
    onToggle?: (ev: Event) => void;
}

interface SectionHeaderElement {
    highlightName: () => void;
    highlightDescription: () => void;
    startHighlightAnimation: () => void;
}

class SectionHeader extends Component<Props> implements SectionHeaderElement {
    static override defaultProps: Partial<Props> = {
        showDescription: true,
        expandOnHeaderClick: true
    };

    highlightOverlayRef = createRef<HTMLDivElement>();
    nameInputRef = createRef();
    descriptionInputRef = createRef();

    get isRichInputDisabled() {
        const { readOnly, actionsDisabled } = this.props;

        return actionsDisabled || readOnly;
    }

    public highlightName = () => {
        highlightInput(this.nameInputRef.current);
    };

    public highlightDescription = () => {
        highlightInput(this.descriptionInputRef.current);
    };

    public startHighlightAnimation = () => {
        this.highlightOverlayRef.current?.animate([
            { backgroundColor: "var(--overlay-selected)" },
            { backgroundColor: "transparent" }
        ], {
            duration: 2000,
            easing: "cubic-bezier(1, 0, 0.4, 0)"
        });
    };

    handleNameBlur = (_: Event, data: RichInputBlurData<string>) => {
        this.props.onNameBlur?.(data);
    };

    handleDescriptionBlur = (_: Event, data: RichInputBlurData<string>) => {
        this.props.onDescriptionBlur?.(data);
    };

    handleClick = (ev: Event) => {
        const { collapsed, expandOnHeaderClick, onToggle } = this.props;

        if (!collapsed || !expandOnHeaderClick) {
            return;
        }

        onToggle?.(ev);
    };

    renderElevator = () => {
        const {
            id,
            depth = 0,
            maximumDepthLimit = 0,
            elevatorProps,
            hasDragHover
        } = this.props;

        if (!elevatorProps || hasDragHover) {
            return null;
        }

        const { class: elevatorClass } = elevatorProps;

        return (
            <SectionElevator
                {...elevatorProps}
                class={classname(styles.elevator, elevatorClass)}
                icon={<IconDragSmall/>}
                hoveredSectionId={id}
                hoveredSectionDepth={depth}
                maximumDepthLimit={maximumDepthLimit}
                shouldAdaptPosition={true}
                animation="fade"/>
        );
    };

    renderInfo = () => {
        const { info, emptyText, infoText, collapsed } = this.props;

        if (info) {
            return info;
        }

        const computedInfoText = emptyText || (collapsed && infoText);

        if (!computedInfoText) {
            return null;
        }

        return <span class={classname("caption-1", styles.info)}>{computedInfoText}</span>;
    };

    renderToggleButton = () => {
        const { emptyText, isToggleDisabled, onToggle } = this.props;

        if (emptyText || isToggleDisabled) {
            return null;
        }

        return (
            <OnlyIconButton
                class={classname(styles.actionButton, styles.toggleButton)}
                variant={OnlyIconButtonVariant.Ninja}
                icon={IconArrowDown}
                onClick={onToggle}/>
        );
    };

    renderDescription = () => {
        const {
            description,
            descriptionContent,
            showDescription,
            collapsed
        } = this.props;

        if (!showDescription) {
            return null;
        }

        // When 'descriptionContent' is provided, don't take the collapsed into account
        if (descriptionContent) {
            return descriptionContent;
        }

        // By default, don't show the description when collapsed
        if (!description || collapsed) {
            return null;
        }

        return (
            <RichInput<string>
                class={classname(styles.input, styles.descriptionInput)}
                value={description}
                variant={InputVariant.Ninja}
                disabled={this.isRichInputDisabled}
                inputRef={this.descriptionInputRef}
                formatter={formatTextArea}
                maxLength={100}
                onBlur={this.handleDescriptionBlur}/>
        );
    };

    renderAdditionalInfo = () => {
        const { additionalInfo, collapsed } = this.props;

        if (!additionalInfo || collapsed) {
            return null;
        }

        return additionalInfo;
    };

    renderActionButtons = () => {
        const { additionalActionButtons, optionsMenuButton, actionsDisabled } = this.props;

        if (actionsDisabled) {
            return null;
        }

        return (
            <div
                class={styles.actionButtons}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}>
                {additionalActionButtons}
                {this.renderToggleButton()}
                {optionsMenuButton}
            </div>
        );
    };

    render() {
        const {
            class: cssClass,
            style,
            depth = 0,
            name,
            summaryContent,
            filterValue,
            collapsed,
            hasDragHover,
            expandOnHeaderClick
        } = this.props;

        return (
            <div
                class={classname(styles.sectionHeader, cssClass, {
                    [styles.collapsed]: collapsed,
                    [styles.dragHover]: hasDragHover,
                    [styles.nested]: Boolean(depth),
                    [styles.clickable]: expandOnHeaderClick
                })}
                style={style}
                onClick={this.handleClick}>
                <div class={styles.content}>
                    {this.renderElevator()}
                    <div class={styles.main}>
                        <div class={styles.nameAndInfo}>
                            <div class={styles.nameInputContainer}>
                                <RichInput<string>
                                    class={styles.input}
                                    value={name}
                                    variant={InputVariant.Ninja}
                                    textClass={depth ? "heading-3" : "heading-2"}
                                    filterValue={filterValue}
                                    maxLength={100}
                                    disabled={this.isRichInputDisabled}
                                    required
                                    enableWordSearch
                                    inputRef={this.nameInputRef}
                                    onClick={stopPropagation}
                                    onBlur={this.handleNameBlur}/>
                            </div>
                            {this.renderInfo()}
                        </div>
                        {this.renderActionButtons()}
                    </div>
                    {summaryContent}
                    {this.renderDescription()}
                    {this.renderAdditionalInfo()}
                </div>
                <div class={styles.highlightOverlay} ref={this.highlightOverlayRef}/>
            </div>
        );
    }
}

interface WithComponentRefProps extends Props {
    sectionHeaderRef?: RefCallback<SectionHeaderElement>;
}

const withComponentRef = (BaseComponent: typeof SectionHeader) => (props: WithComponentRefProps) => {
    const newProps = Object.assign({}, props, { ref: props.sectionHeaderRef });

    delete newProps.sectionHeaderRef;

    return <BaseComponent {...newProps}/>;
};

export default withComponentRef(SectionHeader);
export {
    SectionHeaderElement,
    Props as SectionHeaderProps
};
